'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface PricingFeature {
  text: string;
  included: boolean;
}

interface PricingCardProps {
  title: string;
  subtitle: string;
  price: string;
  period: string;
  features: PricingFeature[];
  isPopular?: boolean;
  ctaText: string;
  onSelect: () => void;
}

export default function PricingCard({
  title,
  subtitle,
  price,
  period,
  features,
  isPopular = false,
  ctaText,
  onSelect,
}: PricingCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={`relative bg-white rounded-2xl p-8 transition-all duration-300 border mt-20 ${
        isPopular
          ? 'border-[#42AF47]/50 shadow-xl shadow-[#42AF47]/15 scale-[1.03] md:scale-105'
          : 'border-gray-200 hover:border-[#42AF47]/30 hover:shadow-xl hover:-translate-y-2'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isPopular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#42AF47] text-white px-6 py-2 rounded-full text-sm font-heading font-semibold shadow-md">
          Most Popular
        </div>
      )}

      <div className="text-center mb-8">
        <h3 className="text-2xl font-heading font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600">{subtitle}</p>
      </div>

      <div className="text-center mb-8">
        <div className="flex items-baseline justify-center">
          <span className="text-5xl font-heading font-bold text-[#42AF47]">{price}</span>
          <span className="text-gray-600 ml-2">{period}</span>
        </div>
      </div>

      <ul className="space-y-4 mb-10">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start space-x-3">
            <Icon
              name={feature.included ? 'CheckCircleIcon' : 'XCircleIcon'}
              variant="solid"
              size={20}
              className={
                feature.included
                  ? 'text-[#42AF47] mt-0.5'
                  : 'text-gray-400 mt-0.5'
              }
            />
            <span
              className={`text-sm ${
                feature.included ? 'text-gray-800' : 'text-gray-500 line-through'
              }`}
            >
              {feature.text}
            </span>
          </li>
        ))}
      </ul>

      <button
        onClick={onSelect}
        className={`w-full py-3.5 rounded-lg font-heading font-semibold text-base transition-all duration-300 ${
          isPopular
            ? 'bg-[#42AF47] text-white shadow-lg hover:shadow-xl hover:brightness-105 hover:-translate-y-0.5'
            : 'bg-gray-800 text-white hover:bg-gray-900'
        }`}
      >
        {ctaText}
      </button>
    </div>
  );
}